import { debugLog } from './debug-log';
/**
 * IndexedDB Cache System for Multi-Service Usage Dashboard
 * Supports OpenAI, Anthropic, Gemini, GitHub, and future services
 */

// Database configuration
const DB_NAME = 'usage_dashboard_cache';
const DB_VERSION = 2;

// Object stores
const STORES = {
  CACHE: 'cache_entries',
  METADATA: 'cache_metadata'
} as const;

// Service types
export type ServiceType = 'openai' | 'anthropic' | 'gemini' | 'github';

// Cache entry structure
export interface CacheEntry<T = unknown> {
  id: string; // Composite key: {service}_{endpoint}_{params_hash}
  service: ServiceType;
  endpoint: string; // e.g., 'organization_usage_details', 'projects', 'users'
  params?: Record<string, unknown>; // Original request parameters for range-aware filtering
  paramsHash: string; // Hash of request parameters (date range, filters, etc.)
  data: T; // Cached response data
  timestamp: number; // Creation timestamp
  expiresAt: number | null; // null explicitly retains finalized history until clear
  size: number; // Data size in bytes
  accessCount: number; // Number of times accessed
  lastAccessed: number; // Last access timestamp
}

// Cache metadata for statistics and management
export interface CacheMetadata {
  id: string;
  service: ServiceType;
  totalEntries: number;
  totalSize: number;
  lastCleanup: number;
  createdAt: number;
}

// Cache configuration per service
export interface ServiceCacheConfig {
  defaultTTL: number; // Default time-to-live in milliseconds
  maxEntries: number; // Maximum cache entries per service
  maxSize: number; // Maximum total size per service in bytes
}

// Default cache configurations
export const DEFAULT_CACHE_CONFIGS: Record<ServiceType, ServiceCacheConfig> = {
  openai: {
    defaultTTL: 60 * 60 * 1000, // 1 hour
    maxEntries: 100,
    maxSize: 50 * 1024 * 1024 // 50MB
  },
  anthropic: {
    defaultTTL: 60 * 60 * 1000, // 1 hour
    maxEntries: 100,
    maxSize: 50 * 1024 * 1024 // 50MB
  },
  gemini: {
    defaultTTL: 60 * 60 * 1000, // 1 hour
    maxEntries: 100,
    maxSize: 50 * 1024 * 1024 // 50MB
  },
  github: {
    defaultTTL: 30 * 60 * 1000, // 30 minutes (shorter due to rate limits)
    maxEntries: 50,
    maxSize: 25 * 1024 * 1024 // 25MB
  }
};

// IndexedDB wrapper class
export class IndexedDBCache {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private isSupported = typeof indexedDB !== 'undefined';

  constructor() {
    this.initPromise = this.init().catch((error) => {
      console.error('IndexedDB initialization failed; cache will be disabled in this environment.', error);
    });
  }

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (!this.isSupported) {
      console.warn('IndexedDB is not available. Running without persistent cache.');
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      let abandoned = false;

      request.onblocked = () => {
        abandoned = true;
        reject(new Error('Cache upgrade blocked by an older tab; persistence disabled until reload'));
      };

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        if (abandoned) { request.result.close(); return; }
        this.db = request.result;
        this.db.onversionchange = () => this.close();
        debugLog('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create cache entries store
        if (!db.objectStoreNames.contains(STORES.CACHE)) {
          const cacheStore = db.createObjectStore(STORES.CACHE, { keyPath: 'id' });
          
          // Create indexes for efficient querying
          cacheStore.createIndex('service', 'service', { unique: false });
          cacheStore.createIndex('endpoint', 'endpoint', { unique: false });
          cacheStore.createIndex('service_endpoint', ['service', 'endpoint'], { unique: false });
          cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
          cacheStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }

        // Create metadata store
        if (!db.objectStoreNames.contains(STORES.METADATA)) {
          const metadataStore = db.createObjectStore(STORES.METADATA, { keyPath: 'id' });
          metadataStore.createIndex('service', 'service', { unique: true });
        }

        const cacheStore = request.transaction!.objectStore(STORES.CACHE);
        if (!cacheStore.indexNames.contains('credential_range')) {
          cacheStore.createIndex('credential_range', [
            'service', 'endpoint', 'params.credentialRevision', 'params.bucketWidth', 'params.startTime'
          ]);
        }

        debugLog('IndexedDB schema created');
      };
    });
  }

  /**
   * Ensure database is ready for use.
   * Returns false when IndexedDB is unavailable in this runtime.
   */
  private async ensureReady(): Promise<boolean> {
    if (!this.isSupported) {
      return false;
    }
    if (this.initPromise) {
      await this.initPromise;
    }
    return this.db !== null;
  }

  /**
   * Generate cache key from service, endpoint, and parameters
   */
  private generateCacheKey(service: ServiceType, endpoint: string, params: Record<string, unknown> = {}): string {
    const paramsStr = JSON.stringify(params, Object.keys(params).sort());
    const paramsHash = this.simpleHash(paramsStr);
    return `${service}_${endpoint}_${paramsHash}`;
  }

  /**
   * Simple hash function for parameters
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Calculate data size in bytes
   */
  private calculateDataSize(data: unknown): number {
    return new Blob([JSON.stringify(data)]).size;
  }

  /**
   * Store data in cache
   */
  async set(
    service: ServiceType,
    endpoint: string,
    data: unknown,
    params: Record<string, unknown> = {},
    customTTL?: number | null
  ): Promise<boolean> {
    return this.setMany(service, endpoint, [{ data, params, ttl: customTTL }]);
  }

  /** Batch persistence commits atomically; metadata is updated once per batch. */
  async setMany(
    service: ServiceType,
    endpoint: string,
    values: Array<{ data: unknown; params: Record<string, unknown>; ttl?: number | null }>
  ): Promise<boolean> {
    if (!(await this.ensureReady())) {
      return false;
    }

    try {
      const config = DEFAULT_CACHE_CONFIGS[service];
      const now = Date.now();
      const entries = values.map(({ data, params, ttl }): CacheEntry => ({
        id: this.generateCacheKey(service, endpoint, params),
        service,
        endpoint,
        params,
        paramsHash: this.simpleHash(JSON.stringify(params, Object.keys(params).sort())),
        data,
        timestamp: now,
        expiresAt: ttl === null ? null : now + (ttl ?? config.defaultTTL),
        size: this.calculateDataSize(data),
        accessCount: 0,
        lastAccessed: now
      }));

      const transaction = this.db!.transaction([STORES.CACHE, STORES.METADATA], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      
      await new Promise<void>((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onabort = () => reject(transaction.error ?? new Error('Cache transaction aborted'));
        transaction.onerror = () => reject(transaction.error);
        const metadataStore = transaction.objectStore(STORES.METADATA);
        const metadataRequest = metadataStore.get(service);
        metadataRequest.onsuccess = () => {
          const metadata: CacheMetadata = metadataRequest.result ?? { id: service, service,
            totalEntries: 0, totalSize: 0, lastCleanup: now, createdAt: now };
          // Update only the touched keys, not every retained historical payload.
          // Deduplicate keys so overwrite deltas are correct within this batch.
          const uniqueEntries = [...new Map(entries.map(entry => [entry.id, entry])).values()];
          let remaining = uniqueEntries.length;
          for (const entry of uniqueEntries) {
            const previousRequest = store.get(entry.id);
            previousRequest.onsuccess = () => {
              const previous = previousRequest.result as CacheEntry | undefined;
              metadata.totalEntries += previous ? 0 : 1;
              metadata.totalSize += entry.size - (previous?.size ?? 0);
              store.put(entry);
              if (--remaining === 0) metadataStore.put(metadata);
            };
          }
        };
      });
      
      debugLog(`Cache stored: ${entries.length} ${service}:${endpoint} entries`);
      return true;
    } catch (error) {
      console.error('Failed to store cache:', error);
      return false;
    }
  }

  /**
   * Retrieve data from cache
   */
  async get<T = unknown>(service: ServiceType, endpoint: string, params: Record<string, unknown> = {}): Promise<T | null> {
    if (!(await this.ensureReady())) {
      return null;
    }

    try {
      const cacheKey = this.generateCacheKey(service, endpoint, params);
      const transaction = this.db!.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      
      const entry = await new Promise<CacheEntry<T> | null>((resolve, reject) => {
        const request = store.get(cacheKey);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });

      if (!entry) {
        debugLog(`Cache miss: ${cacheKey}`);
        return null;
      }

      // A short hash is an index, not proof of resource/credential identity.
      // Colliding entries may evict each other, but must never cross scopes.
      const canonicalParams = (value: Record<string, unknown>) => JSON.stringify(value, Object.keys(value).sort());
      if (entry.service !== service || entry.endpoint !== endpoint
        || canonicalParams(entry.params ?? {}) !== canonicalParams(params)) return null;

      const now = Date.now();
      
      // Check if expired
      if (entry.expiresAt !== null && now >= entry.expiresAt) {
        debugLog(`Cache expired: ${cacheKey}, age: ${Math.round((now - entry.timestamp) / 1000)}s`);
        await this.delete(service, endpoint, params);
        return null;
      }

      // Update access statistics
      entry.accessCount++;
      entry.lastAccessed = now;
      
      await new Promise<void>((resolve, reject) => {
        const updateRequest = store.put(entry);
        updateRequest.onsuccess = () => resolve();
        updateRequest.onerror = () => reject(updateRequest.error);
      });

      const age = now - entry.timestamp;
      const remainingTTL = entry.expiresAt === null ? Infinity : entry.expiresAt - now;
      debugLog(`Cache hit: ${cacheKey}, age: ${Math.round(age / 1000)}s, expires in: ${Math.round(remainingTTL / 1000)}s`);
      
      return entry.data;
    } catch (error) {
      console.error('Failed to retrieve cache:', error);
      return null;
    }
  }

  /**
   * List cache entries for a service + endpoint
   */
  async list<T = unknown>(service: ServiceType, endpoint: string, throwOnError = false): Promise<CacheEntry<T>[]> {
    if (!(await this.ensureReady())) {
      if (throwOnError) throw new Error('Usage cache is unavailable');
      return [];
    }

    try {
      const transaction = this.db!.transaction([STORES.CACHE], 'readonly');
      const store = transaction.objectStore(STORES.CACHE);
      const index = store.index('service_endpoint');
      const now = Date.now();
      const entries: CacheEntry<T>[] = [];

      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.only([service, endpoint]));
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const entry = cursor.value as CacheEntry<T>;
            if (entry.expiresAt === null || entry.expiresAt > now) {
              entries.push(entry);
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      return entries;
    } catch (error) {
      console.error(`Failed to list cache entries for ${service}:${endpoint}:`, error);
      if (throwOnError) throw error;
      return [];
    }
  }

  /** Indexed overlap lookup. Stored segment spans must not exceed maxSpanSeconds.
   * Cost days use one day; usage uses the provider's 168-hour/31-day bound.
   * Work is proportional to the selected range, not all retained history.
   */
  async listRange<T>(service: ServiceType, endpoint: string, credentialRevision: string,
    bucketWidth: string, startTime: number, endTime: number, maxSpanSeconds: number): Promise<CacheEntry<T>[]> {
    if (!(await this.ensureReady()) || endTime <= startTime) return [];
    try {
      const prefix = [service, endpoint, credentialRevision, bucketWidth];
      const range = IDBKeyRange.bound([...prefix, startTime - maxSpanSeconds], [...prefix, endTime], false, true);
      const transaction = this.db!.transaction(STORES.CACHE, 'readonly');
      const entries = await new Promise<CacheEntry<T>[]>((resolve, reject) => {
        const request = transaction.objectStore(STORES.CACHE).index('credential_range').getAll(range);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });
      return entries.filter(entry => (entry.expiresAt === null || entry.expiresAt > Date.now())
        && Number(entry.params?.endTime) > startTime);
    } catch (error) {
      console.warn('Range cache unavailable; falling back to provider fetch', error);
      return [];
    }
  }

  /**
   * Delete specific cache entry
   */
  async delete(service: ServiceType, endpoint: string, params: Record<string, unknown> = {}): Promise<boolean> {
    if (!(await this.ensureReady())) {
      return false;
    }

    try {
      const cacheKey = this.generateCacheKey(service, endpoint, params);
      const transaction = this.db!.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      
      await new Promise<void>((resolve, reject) => {
        const request = store.delete(cacheKey);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await this.updateMetadata(service);
      return true;
    } catch (error) {
      console.error('Failed to delete cache:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries for a specific service
   */
  async clearService(service: ServiceType): Promise<number> {
    if (!(await this.ensureReady())) {
      return 0;
    }

    try {
      const transaction = this.db!.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      const index = store.index('service');
      
      let deletedCount = 0;
      
      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.only(service));
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      await this.updateMetadata(service);
      debugLog(`Cleared ${deletedCount} cache entries for service: ${service}`);
      return deletedCount;
    } catch (error) {
      console.error(`Failed to clear cache for service ${service}:`, error);
      return 0;
    }
  }

  /**
   * Clear all expired cache entries
   */
  async clearExpired(): Promise<number> {
    if (!(await this.ensureReady())) {
      return 0;
    }

    try {
      const now = Date.now();
      const transaction = this.db!.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      const index = store.index('expiresAt');
      
      let deletedCount = 0;
      const serviceUpdates = new Set<ServiceType>();
      
      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.upperBound(now));
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const entry = cursor.value as CacheEntry;
            serviceUpdates.add(entry.service);
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      // Update metadata for affected services
      for (const service of serviceUpdates) {
        await this.updateMetadata(service);
      }

      if (deletedCount > 0) {
        debugLog(`Cache cleanup completed: ${deletedCount} expired entries cleared`);
      }
      return deletedCount;
    } catch (error) {
      console.error('Failed to clear expired cache:', error);
      return 0;
    }
  }

  /**
   * Clear all cache entries
   */
  async clearAll(): Promise<number> {
    if (!(await this.ensureReady())) {
      return 0;
    }

    try {
      const transaction = this.db!.transaction([STORES.CACHE], 'readwrite');
      const store = transaction.objectStore(STORES.CACHE);
      
      const countRequest = store.count();
      const totalCount = await new Promise<number>((resolve, reject) => {
        countRequest.onsuccess = () => resolve(countRequest.result);
        countRequest.onerror = () => reject(countRequest.error);
      });
      
      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      // Clear metadata
      const metadataTransaction = this.db!.transaction([STORES.METADATA], 'readwrite');
      const metadataStore = metadataTransaction.objectStore(STORES.METADATA);
      await new Promise<void>((resolve, reject) => {
        const request = metadataStore.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      debugLog(`All cache cleared: ${totalCount} entries removed`);
      return totalCount;
    } catch (error) {
      console.error('Failed to clear all cache:', error);
      return 0;
    }
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<Record<ServiceType, CacheMetadata> & { total: CacheMetadata }> {
    if (!(await this.ensureReady())) {
      const emptyStats = {} as Record<ServiceType, CacheMetadata> & { total: CacheMetadata };
      for (const service of Object.keys(DEFAULT_CACHE_CONFIGS) as ServiceType[]) {
        emptyStats[service] = {
          id: service,
          service,
          totalEntries: 0,
          totalSize: 0,
          lastCleanup: 0,
          createdAt: Date.now()
        };
      }
      emptyStats.total = {
        id: 'total',
        service: 'openai',
        totalEntries: 0,
        totalSize: 0,
        lastCleanup: 0,
        createdAt: Date.now()
      };
      return emptyStats;
    }

    try {
      const stats: Record<ServiceType, CacheMetadata> & { total?: CacheMetadata } = {} as Record<ServiceType, CacheMetadata> & { total?: CacheMetadata };
      
      // Get per-service stats
      for (const service of Object.keys(DEFAULT_CACHE_CONFIGS) as ServiceType[]) {
        const metadata = await this.getServiceMetadata(service);
        stats[service] = metadata;
      }

      // Calculate total stats
      const totalEntries = Object.values(stats).reduce((sum, meta) => sum + meta.totalEntries, 0);
      const totalSize = Object.values(stats).reduce((sum, meta) => sum + meta.totalSize, 0);
      const oldestCreated = Math.min(...Object.values(stats).map(meta => meta.createdAt));

      stats.total = {
        id: 'total',
        service: 'openai', // Not applicable for total
        totalEntries,
        totalSize,
        lastCleanup: Math.max(...Object.values(stats).map(meta => meta.lastCleanup)),
        createdAt: oldestCreated
      };

      return stats as Record<ServiceType, CacheMetadata> & { total: CacheMetadata };
    } catch (error) {
      console.error('Failed to get cache stats:', error);
      // Return empty stats
      const emptyStats = {} as Record<ServiceType, CacheMetadata> & { total: CacheMetadata };
      for (const service of Object.keys(DEFAULT_CACHE_CONFIGS) as ServiceType[]) {
        emptyStats[service] = {
          id: service,
          service,
          totalEntries: 0,
          totalSize: 0,
          lastCleanup: 0,
          createdAt: Date.now()
        };
      }
      emptyStats.total = {
        id: 'total',
        service: 'openai',
        totalEntries: 0,
        totalSize: 0,
        lastCleanup: 0,
        createdAt: Date.now()
      };
      return emptyStats;
    }
  }

  /**
   * Update metadata for a service
   */
  private async updateMetadata(service: ServiceType): Promise<void> {
    try {
      const transaction = this.db!.transaction([STORES.CACHE, STORES.METADATA], 'readwrite');
      const cacheStore = transaction.objectStore(STORES.CACHE);
      const metadataStore = transaction.objectStore(STORES.METADATA);
      const index = cacheStore.index('service');
      
      let totalEntries = 0;
      let totalSize = 0;
      
      await new Promise<void>((resolve, reject) => {
        const request = index.openCursor(IDBKeyRange.only(service));
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const entry = cursor.value as CacheEntry;
            totalEntries++;
            totalSize += entry.size;
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      // Get existing metadata or create new
      const existingMetadata = await new Promise<CacheMetadata | null>((resolve, reject) => {
        const request = metadataStore.get(service);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });

      const metadata: CacheMetadata = {
        id: service,
        service,
        totalEntries,
        totalSize,
        lastCleanup: Date.now(),
        createdAt: existingMetadata?.createdAt || Date.now()
      };

      await new Promise<void>((resolve, reject) => {
        const request = metadataStore.put(metadata);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error(`Failed to update metadata for service ${service}:`, error);
    }
  }

  /**
   * Get metadata for a specific service
   */
  private async getServiceMetadata(service: ServiceType): Promise<CacheMetadata> {
    try {
      const transaction = this.db!.transaction([STORES.METADATA], 'readonly');
      const store = transaction.objectStore(STORES.METADATA);
      
      const metadata = await new Promise<CacheMetadata | null>((resolve, reject) => {
        const request = store.get(service);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });

      return metadata || {
        id: service,
        service,
        totalEntries: 0,
        totalSize: 0,
        lastCleanup: 0,
        createdAt: Date.now()
      };
    } catch (error) {
      console.error(`Failed to get metadata for service ${service}:`, error);
      return {
        id: service,
        service,
        totalEntries: 0,
        totalSize: 0,
        lastCleanup: 0,
        createdAt: Date.now()
      };
    }
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Singleton instance
let cacheInstance: IndexedDBCache | null = null;

/**
 * Get singleton cache instance
 */
export const getCache = (): IndexedDBCache => {
  if (!cacheInstance) {
    cacheInstance = new IndexedDBCache();
  }
  return cacheInstance;
};
